import SettingsService from "../../services/SettingsService"
import Geolocation from "../../models/Geolocation"
import GeolocationService, {GeoCallback} from "../../services/GeolocationService"
import NativeInterface from "../../services/NativeInterface"
import {AudioController} from "./AudioController"
import {renderMarkdownHtml} from 'components/Markdown'
import {LanguageContextType} from 'pages/dimension/LanguageContext'

export interface ContentProps {
    langCtx: LanguageContextType
    dimension: string
    env: string
    content?: any
    logAnalyticsEvent?: (event: string, params: any) => void
}

export interface MarkerInfo {
    info: any,
    gmark?: google.maps.Marker
    gpoly?: google.maps.Polygon
    tags: string[]
}

const CATEGORY_ALL = 'All'

export class ContentLayer {
    langCtx: LanguageContextType
    map: google.maps.Map
    config?: ContentProps
    infoWindow = new google.maps.InfoWindow() // {maxWidth: 300}

    items: MarkerInfo[] = []

    isFilterExpanded = false
    filterControlDiv?: HTMLElement
    selectedCategories: string[] = []
    categoryTags: { [key: string]: string[] } = {}
    mLocation: Geolocation | null = null

    mAudioController?: AudioController

    mGeolocationCallback?: GeoCallback

    constructor(langCtx: LanguageContextType, map: google.maps.Map, config: ContentProps | undefined) {
        this.langCtx = langCtx
        this.map = map
        
        this.setLocation.bind(this)
        if (config) {
            this.mGeolocationCallback = this.setLocation.bind(this)
            GeolocationService.subscribeOnUpdate(this.mGeolocationCallback)

            if (NativeInterface.isPWA()) {
                this.mAudioController = new AudioController(config.env, config.dimension, map)
            }
        }

        this.configure(config)
    }

    makeFilterTitle(expanded: boolean) {
        const title = this.langCtx.translate('Activities') || 'Activities'
         return expanded ? `${title} ▲` : `${title} ▼`
    }

    configure(config: ContentProps | undefined) {
        this.config = config
        this.clearAll()
        if (!config?.content?.groups) {
            return
        }
        for (const group of config?.content?.groups) {
            for (const item of group.items) {
                if (item.geometry?.type == 'Point') {
                    this.createPoint(item, group.tags)
                }
                if (item.geometry?.type == 'Polygon') {
                    this.createPolygon(item, group.tags)
                }
            }
        }

        this.categoryTags = {}
        if (config?.content?.categories && config?.content?.categories.length > 0) {
            for (const cat of config?.content?.categories) {
                this.categoryTags[cat.title] = cat.tags
            }
            const categories = Object.keys(this.categoryTags)
            categories.unshift(CATEGORY_ALL)
            this.selectedCategories = categories
            this.filterControlDiv = this.addContentFilter(this.map, categories, this.selectedCategories)
        }
        this.applyCategoryFilter()
    }

    deinitialize() {
        if (this.mGeolocationCallback) {
            GeolocationService.unsubscribeOnUpdate(this.mGeolocationCallback)
            delete this.mGeolocationCallback
        }
        if (this.mAudioController) {
            this.mAudioController.deinitialize()
        }
    }

    setLocation(loc: Geolocation | null) {
        this.mLocation = loc;
        this.updateAudioPlaybacks()
    }

    updateAudioPlaybacks() {
        if (!this.mAudioController) {
            return
        }

        if (!this.mLocation) {
            return
        }

        if (!google.maps.geometry) {
            // Sometimes we get TypeError: Cannot read properties of undefined (reading 'spherical')
            // My guess is google.maps.geometry is loaded later than this component
            return
        }

        const pos = new google.maps.LatLng(this.mLocation.latitude, this.mLocation.longitude)

        let playlist: { [key: string]: MarkerInfo } = {}
        for (const item of this.items) {
            if (!item.info.audio || !item.info.audio.autoplay_distance) {
                continue
            }
            if (item.gpoly) {
                continue
            }
            if (!item.gmark || !item.gmark.getVisible()) {
                continue
            }

            const markPos = item.gmark.getPosition()
            if (!markPos) {
                continue
            }

            const distance = google.maps.geometry.spherical.computeDistanceBetween(markPos, pos);
            if (distance > item.info.audio.autoplay_distance + 15) {
                continue
            }

            let key = item.gmark.getPosition()?.toString()
            if (!key) {
                continue
            }

            if (distance > item.info.audio.autoplay_distance && !this.mAudioController.checkKey(key)) {
                continue
            }

            if (!this.mAudioController.checkKey(key)) {
                if (this.config?.logAnalyticsEvent && item.info.id) {
                    this.config.logAnalyticsEvent('WebMapItemAudioPlay', {object_id: item.info.id})
                }
            }

            playlist[key] = item.info.audio
        }

        this.mAudioController.updatePlaylist(playlist)
    }

    clearAll() {
        for (const item of this.items) {
            if (item.gmark) {
                item.gmark.setMap(null)
            }
            if (item.gpoly) {
                item.gpoly.setMap(null)
            }
        }
        this.items = []

        if (this.filterControlDiv) {
            this.removeMapControl(this.map, this.filterControlDiv)
            this.filterControlDiv = undefined
        }
    }

    createPoint(item: any, groupTags: string[]) {
        let marker = new google.maps.Marker({
            map: this.map,
            position: item.geometry.coords,
            icon: item.info.icon ? this.makeUrl(item.info.icon) : undefined,
            title: item.info.title,
            //draggable: false
        });
        marker.addListener('click', (event: any) => this.onItemClick(event, item, marker));
        this.items.push({
            info: item,
            gmark: marker,
            tags: item.tags ? [...groupTags, ...item.tags] : groupTags
        })
    }

    createPolygon(item: any, groupTags: string[]) {
        const poly = new google.maps.Polygon({
            map: this.map,
            paths: item.geometry.coords,
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 0.35
            //editable: true
          });
          poly.addListener('click', (event: any) => this.onItemClick(event, item, poly));

          this.items.push({
            info: item,
            gpoly: poly,
            tags: item.tags ? [...groupTags, ...item.tags] : groupTags
        })
    }

    onItemLinkClick(event: any) {
        let itemId = event.currentTarget.dataset.itemid;
        console.log(`Map item link clicked: ${itemId}`)
        if (this.config?.logAnalyticsEvent && itemId) {
            this.config.logAnalyticsEvent('WebMapItemLinkClick', {object_id: itemId})
        }
        //event.preventDefault();
        //window.open(item.info.link_address, '_blank');
    }

    async onItemClick(event: any, item: any, anchor: google.maps.MVCObject) {
        if (this.config?.logAnalyticsEvent && item.id) {
            this.config.logAnalyticsEvent('WebMapItemClick', {object_id: item.id})
        }
        this.infoWindow.setPosition(event.latLng)
        this.infoWindow.setContent(await this.makePopupContent(item));

        if (item.info.link_address) {
            google.maps.event.addListener(this.infoWindow, 'domready', () => {
                const link = document.getElementById('itemInfoLink');
                if (link && !link.dataset.listenerAttached) {
                    link.addEventListener('click', (e) => this.onItemLinkClick(e));
                    link.dataset.listenerAttached = 'true';
                }
            })
        }

        this.infoWindow.open(undefined, anchor);
    }

    onMapClick(event: google.maps.MapMouseEvent) {
        this.infoWindow.close();
        //this.setLocation(new Geolocation(`${event.latLng.lat()} ${event.latLng.lng()} 0 -1 -1`))
    };

    getBounds() {
        if (this.items.length == 0) {
            return null
        }

        let bounds = new google.maps.LatLngBounds();
        for (const item of this.items) {
            if (item.gmark) {
                bounds.extend(item.gmark.getPosition()!);
            }
            if (item.gpoly) {
                item.gpoly.getPath().forEach(point => bounds.extend(point))
            }
        }

        return bounds
    }

    removeMapControl(map: google.maps.Map, control: HTMLElement) {
        for (const position in map.controls) {
            const controls = map.controls[position];
            for (let i = 0; i < controls.getLength(); i++) {
                if (controls.getAt(i) === control) {
                    controls.removeAt(i);
                    return;
                }
            }
        }
    }

    filterChange(category: string, isChecked: boolean) {
        console.log(`filterChange: ${category} ${isChecked}`)
        if (category == CATEGORY_ALL) {
            if (isChecked) {
                this.selectedCategories = this.config?.content?.categories.map((item: any) => item.title);
            } else {
                this.selectedCategories = []
            }

            const checkboxes = this.filterControlDiv?.querySelectorAll('input[type="checkbox"]');
            checkboxes?.forEach((checkbox) => {
                (checkbox as HTMLInputElement).checked = isChecked;
            });
        } else {
            if (isChecked && !this.selectedCategories.includes(category)) {
                this.selectedCategories.push(category)
            } else if (!isChecked && this.selectedCategories.includes(category)) {
                this.selectedCategories = this.selectedCategories.filter(c => c !== category)
            }

        }
        this.applyCategoryFilter();
    }

    applyCategoryFilter() {
        let filterTags = new Set()
        for (const cat of this.selectedCategories) {
            let tags = this.categoryTags[cat]
            if (tags) {
                this.categoryTags[cat].forEach(tag => filterTags.add(tag));
            }
        }

        for (const item of this.items) {
            let visible = item.tags.some(tag => filterTags.has(tag));
            if (item.gmark) {
                item.gmark.setVisible(visible)
            }
            if (item.gpoly) {
                item.gpoly.setVisible(visible)
            }
        }

        this.updateAudioPlaybacks()
    }

    addContentFilter(map: google.maps.Map, categories: string[], selectedCategories: string[]) {
        const controlStyles: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',

            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            borderRadius: '4px',
            overflow: 'hidden', // Ensure child elements don't spill out

            marginTop: '10px',
            marginLeft: '10px',
            backgroundColor: 'white',
            padding: '5px',
            border: '1px solid #ccc',
            //borderRadius: '4px',
            //display: 'flex',
            //alignItems: 'center',
            justifyContent: 'space-between',
            fontFamily: 'Arial, sans-serif', // Resetting the font
            fontSize: '14px',                // Specifying font size
            color: '#333',                   // Specifying text color
            boxSizing: 'border-box',         // Resetting box-sizing
            lineHeight: 'normal'             // Resetting line-height
        };

        const controlDiv = document.createElement('div');
        for (const [key, value] of Object.entries(controlStyles)) {
            // @ts-ignore
            controlDiv.style[key] = value;
        }

        // Collapsible Header with arrow
        const collapsibleHeader = document.createElement('button');
        collapsibleHeader.innerText = this.makeFilterTitle(this.isFilterExpanded);
        collapsibleHeader.style.width = '100%';
        collapsibleHeader.style.textAlign = 'left';
        collapsibleHeader.style.border = 'none';
        collapsibleHeader.style.backgroundColor = '#f4f4f4';
        collapsibleHeader.style.cursor = 'pointer';
        collapsibleHeader.style.padding = '10px';
        collapsibleHeader.style.transition = 'background-color 0.3s ease';

        collapsibleHeader.onmouseover = () => {
            collapsibleHeader.style.backgroundColor = '#e4e4e4';
        };
        
        collapsibleHeader.onmouseout = () => {
            collapsibleHeader.style.backgroundColor = '#f4f4f4';
        };

        collapsibleHeader.onclick = () => {
            this.isFilterExpanded = !this.isFilterExpanded
            categoryList.style.display = this.isFilterExpanded ? 'block' : 'none';
            collapsibleHeader.innerText = this.makeFilterTitle(this.isFilterExpanded);
        };
        
        controlDiv.appendChild(collapsibleHeader);

        const categoryList = document.createElement('div');
        categoryList.style.display = this.isFilterExpanded ? 'block' : 'none';
        categoryList.style.marginTop = '10px'; // Space between button and checkboxes
    
        categories.forEach(category => {
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.value = category;
            checkbox.checked = selectedCategories.includes(category);
            checkbox.onchange = () => {
                this.filterChange(category, checkbox.checked)
            };
            checkbox.style.marginRight = '5px';
    
            const label = document.createElement('label');
            label.style.display = 'flex';
            label.style.alignItems = 'center';
            
            const checkboxWrapper = document.createElement('div');
            checkboxWrapper.style.transform = 'scale(1.5)'; // Adjust this value as necessary
            checkboxWrapper.style.marginLeft = '5px';
            checkboxWrapper.style.marginRight = '5px'; // A little margin for better spacing after scaling
            checkboxWrapper.appendChild(checkbox);
            
            label.appendChild(checkboxWrapper);
            label.appendChild(document.createTextNode(this.langCtx.translate(category)));
    
            categoryList.appendChild(label);
            categoryList.appendChild(document.createElement('br'));
        });
    
        controlDiv.appendChild(categoryList);
        map.controls[google.maps.ControlPosition.TOP_LEFT].push(controlDiv);
        return controlDiv
    }

    makeUrl(file: string) {
        return SettingsService.makeFileUrl(this.config?.dimension??'', this.config?.env??'', file)
    }

    makeImageTag(file: string) {
        if (!file || file == '') {
            return ''
        }
        return `<img src="${this.makeUrl(file)}" width="100%" style="padding-top: 5px; "/>`
    }

    async makePopupContent(item: any) {
        if (!item.info) {
            return ''
        }

        const width = item.info.width ? item.info.width : '200px'
        let innerHtml = '';

        if (item.info.markdown) {
            const response = await this.langCtx.fetchLocalized(this.makeUrl(item.info.markdown), {cache: "no-cache"});
            innerHtml = renderMarkdownHtml(await response.text(), this.makeUrl(''))
        } else {
            innerHtml = `<h4>${item.info.title}</h4>
                         <p style="padding-top: 3px; ">${item.info.description}</p>
                         ${item.info.link_text ? `<a id="itemInfoLink" data-itemid="${item.id ? item.id : ''}" href="${item.info.link_address}" class="map-info-link" target=”_blank”>${item.info.link_text}</a>` : ''}
                         ${this.makeImageTag(item.info.image)}`
        }
        return `<div style="width: ${width};">${innerHtml}</div>`
    }

    updateFilterTitle(langCtx: any) {
        if (this.filterControlDiv) {
            const header = this.filterControlDiv.querySelector('button')
            if (header) {
                this.langCtx = langCtx
                header.innerText = this.makeFilterTitle(this.isFilterExpanded)
            }
        }
    }
}
